"use client";

import React, { useState, useEffect } from "react";
import { useGraphStore } from "@/store/graph-store";
import { fetchNodeByIdAction, updateNodeAction } from "@/actions/graph-actions";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import { Button } from "@/components/ui/button";
import { Badge, Skeleton } from "@/components/ui/feedback";
import { AlertCircle, CheckCircle2 } from "lucide-react";

interface DetailsTabProps {
  nodeId: string;
  onClose?: () => void;
}

export function DetailsTab({ nodeId, onClose }: DetailsTabProps) {
  const { nodes, setNodes } = useGraphStore();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form states
  const [nodeType, setNodeType] = useState<string>("");
  const [name, setName] = useState("");

  // Strategy details
  const [goalStatus, setGoalStatus] = useState<any>("healthy");
  const [goalProgress, setGoalProgress] = useState<number>(0);

  // Work details
  const [projectStatus, setProjectStatus] = useState<any>("on_track");
  const [projectBudget, setProjectBudget] = useState("");
  const [projectRevenue, setProjectRevenue] = useState("");

  // People details
  const [agentRole, setAgentRole] = useState<any>("member");

  // Load details from DB on mount/id change
  useEffect(() => {
    let active = true;

    const loadNodeDetails = async () => {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      try {
        const fullNode = await fetchNodeByIdAction(nodeId);
        if (!active) return;

        if (!fullNode) {
          setError(
            "Impossibile trovare le informazioni del nodo nel database.",
          );
          setIsLoading(false);
          return;
        }

        // Initialize state
        setNodeType(fullNode.type);
        setName(fullNode.name);

        const details = fullNode.details || {};
        if (fullNode.type === "strategy") {
          setGoalStatus(details.status || "healthy");
          setGoalProgress(Number(details.progress) || 0);
        } else if (fullNode.type === "work") {
          setProjectStatus(details.status || "on_track");
          setProjectBudget(details.budget ? String(details.budget) : "0");
          setProjectRevenue(details.revenue ? String(details.revenue) : "0");
        } else if (fullNode.type === "people") {
          setAgentRole(details.role || "member");
        }
      } catch (err: any) {
        if (active) {
          setError(err.message || "Errore nel caricamento dei dati.");
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadNodeDetails();

    return () => {
      active = false;
    };
  }, [nodeId]);

  // Handle Form Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    const updatePayload: any = {
      id: nodeId,
      type: nodeType,
      name: name,
    };

    if (nodeType === "strategy") {
      updatePayload.goalTitle = name;
      updatePayload.goalStatus = goalStatus;
      updatePayload.goalProgress = Number(goalProgress);
    } else if (nodeType === "work") {
      updatePayload.projectStatus = projectStatus;
      updatePayload.projectBudget = projectBudget;
      updatePayload.projectRevenue = projectRevenue;
    } else if (nodeType === "people") {
      updatePayload.agentRole = agentRole;
    }

    try {
      const res = await updateNodeAction(updatePayload);
      if (res && "error" in res) {
        setError(res.error);
      } else {
        setSuccessMessage("Nodo salvato con successo.");

        // Instantly update local Zustand store so Canvas reflects the change
        const updatedNodes = nodes.map((n) => {
          if (n.id === nodeId) {
            let status = "healthy";
            let subtext: string | undefined = undefined;

            if (nodeType === "strategy") {
              status = goalStatus;
              subtext = `Progress: ${goalProgress}%`;
            } else if (nodeType === "work") {
              status = projectStatus;
              subtext = `Budget: €${Number(projectBudget).toLocaleString()}`;
            } else if (nodeType === "people") {
              status = "healthy";
              subtext = `Role: ${agentRole}`;
            }

            return {
              ...n,
              data: {
                ...n.data,
                label: name,
                status: status,
                subtext: subtext,
              },
            };
          }
          return n;
        });
        setNodes(updatedNodes);
      }
    } catch (err: any) {
      setError(err.message || "Errore durante il salvataggio.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col h-full space-y-4 p-4 overflow-y-auto"
    >
      {/* Type badge display */}
      <div className="flex items-center justify-between pb-2 border-b border-border/40">
        <span className="text-xs text-text-secondary font-mono">
          ID: {nodeId}
        </span>
        <Badge variant={nodeType as any} className="capitalize text-xs">
          {nodeType}
        </Badge>
      </div>

      {/* Feedback Messages */}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-error-soft text-error text-xs border border-error/20">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-success-soft text-success text-xs border border-success/20">
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{successMessage}</span>
        </div>
      )}

      {/* Common Field: Name */}
      <div className="space-y-1.5">
        <Label htmlFor="node-name">Nome Elemento</Label>
        <Input
          id="node-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Inserisci il nome dell'elemento"
        />
      </div>

      {/* Strategy Node Specifics */}
      {nodeType === "strategy" && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="goal-status">Stato Obiettivo</Label>
            <Select
              id="goal-status"
              value={goalStatus}
              onChange={(e) => setGoalStatus(e.target.value as any)}
            >
              <option value="healthy">Healthy</option>
              <option value="attention">Attention</option>
              <option value="at_risk">At Risk</option>
              <option value="critical">Critical</option>
            </Select>
          </div>

          <div className="space-y-1.5">
            <div className="flex justify-between items-center">
              <Label htmlFor="goal-progress">Progresso (%)</Label>
              <span className="text-xs font-semibold text-text-secondary">
                {goalProgress}%
              </span>
            </div>
            <Input
              id="goal-progress"
              type="number"
              min="0"
              max="100"
              value={goalProgress}
              onChange={(e) =>
                setGoalProgress(
                  Math.min(100, Math.max(0, Number(e.target.value))),
                )
              }
            />
            <input
              type="range"
              min="0"
              max="100"
              value={goalProgress}
              onChange={(e) => setGoalProgress(Number(e.target.value))}
              className="w-full accent-primary-500 cursor-pointer h-1.5 bg-border rounded-lg appearance-none mt-2"
            />
          </div>
        </>
      )}

      {/* Work Node Specifics */}
      {nodeType === "work" && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="project-status">Stato Progetto</Label>
            <Select
              id="project-status"
              value={projectStatus}
              onChange={(e) => setProjectStatus(e.target.value as any)}
            >
              <option value="on_track">On Track</option>
              <option value="watch">Watch</option>
              <option value="at_risk">At Risk</option>
              <option value="blocked">Blocked</option>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="project-budget">Budget (€)</Label>
              <Input
                id="project-budget"
                type="number"
                min="0"
                value={projectBudget}
                onChange={(e) => setProjectBudget(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="project-revenue">Ricavo (€)</Label>
              <Input
                id="project-revenue"
                type="number"
                min="0"
                value={projectRevenue}
                onChange={(e) => setProjectRevenue(e.target.value)}
                placeholder="0"
              />
            </div>
          </div>
        </>
      )}

      {/* People Node Specifics */}
      {nodeType === "people" && (
        <div className="space-y-1.5">
          <Label htmlFor="agent-role">Ruolo Organizzativo</Label>
          <Select
            id="agent-role"
            value={agentRole}
            onChange={(e) => setAgentRole(e.target.value as any)}
          >
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="member">Member</option>
            <option value="viewer">Viewer</option>
          </Select>
        </div>
      )}

      {/* Submit Button */}
      <div className="pt-4 flex gap-2 mt-auto">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1"
          disabled={isSaving}
        >
          Annulla
        </Button>
        <Button type="submit" className="flex-1" loading={isSaving}>
          Salva
        </Button>
      </div>
    </form>
  );
}
